import { ArrowDown, ArrowUp, RotateCcw } from 'lucide-react';
import { useMemo } from 'react';
import { useI18n } from '@/lib/i18n';
import { przestaw } from '@/lib/kolumny-raportu';
import type { OpisKolumny } from '@/lib/raporty-api';

/**
 * WYBÓR KOLUMN I ICH KOLEJNOŚCI — jedna wybieraczka na dwa miejsca.
 *
 * ⚠️ WYCIĄGNIĘTE ZE `WlasnyRaport`, BO EDYTOR HARMONOGRAMU MIAŁ DZIURĘ. Pokazywał listę kolumn
 * bez możliwości zmiany i odsyłał do „okna raportu własnego" — a tamto zapisuje wybór w pamięci
 * PRZEGLĄDARKI, nie w harmonogramie. Czyli raport wysyłany co tydzień jechał zawsze z zestawem
 * domyślnym, mimo że ekran sugerował coś innego. Zamówienie brzmiało „kolumny, które sami chcemy,
 * i jaka kolejność" — bez tego te dwie funkcje się nie spotykały.
 *
 * ⚠️ DWIE LISTY, NIE JEDNA Z ZAZNACZENIAMI: „w pliku" jest listą UPORZĄDKOWANĄ, a w liście
 * z checkboxami nie ma gdzie pokazać porządku.
 *
 * ⚠️ KOMPONENT JEST BEZSTANOWY (`wybrane` + `onZmiana`). Stan trzyma wołający, bo w oknie raportu
 * ląduje w pamięci przeglądarki, a w harmonogramie w bazie — i to są dwie różne trwałości.
 */
export function WyborKolumn({
  katalog,
  domyslne,
  wybrane,
  onZmiana,
  wysokoscWybranych = 'max-h-44',
}: {
  katalog: readonly OpisKolumny[];
  domyslne: readonly string[];
  wybrane: readonly string[];
  onZmiana: (klucze: string[]) => void;
  /** Okno harmonogramu ma więcej pól, więc listę da się tam skrócić. */
  wysokoscWybranych?: string;
}) {
  const { t } = useI18n();
  const wg = useMemo(() => new Map(katalog.map((k) => [k.klucz, k])), [katalog]);
  const dostepne = katalog.filter((k) => !wybrane.includes(k.klucz));

  return (
    <div className="space-y-3">
      <div>
        <p className="mb-1 flex items-baseline justify-between gap-2 text-xs font-medium">
          <span>
            {t('rapw.wybrane')} <span className="text-muted-foreground">({wybrane.length})</span>
          </span>
          <button
            type="button"
            className="flex items-center gap-1 text-xs font-normal text-muted-foreground hover:text-foreground"
            onClick={() => onZmiana([...domyslne])}
          >
            <RotateCcw className="size-3" />
            {t('rapw.domyslne')}
          </button>
        </p>
        {/*
          ⚠️ KOLEJNOŚĆ STRZAŁKAMI, NIE PRZECIĄGANIEM. Przeciąganie działa myszą i tylko myszą;
          strzałki obsługuje też klawiatura, a lista ma kilkanaście pozycji, więc nie ma czego
          optymalizować. Przy okazji nie wciągamy zależności do drag&drop.
        */}
        <div className={`${wysokoscWybranych} overflow-y-auto rounded-md border border-border`}>
          {wybrane.length === 0 && <p className="p-2 text-xs text-muted-foreground">{t('rapw.pusto')}</p>}
          {wybrane.map((klucz, i) => (
            <div key={klucz} className="flex items-center gap-1 border-b border-border/60 px-2 py-1 text-xs last:border-0">
              <span className="w-5 shrink-0 text-right tabular-nums text-muted-foreground">{i + 1}.</span>
              <span className="min-w-0 flex-1 truncate">{wg.get(klucz)?.naglowek ?? klucz}</span>
              <button
                type="button"
                disabled={i === 0}
                title={t('rapw.wyzej')}
                onClick={() => onZmiana(przestaw(wybrane, i, -1))}
                className="rounded p-0.5 hover:bg-accent disabled:opacity-30"
              >
                <ArrowUp className="size-3.5" />
              </button>
              <button
                type="button"
                disabled={i === wybrane.length - 1}
                title={t('rapw.nizej')}
                onClick={() => onZmiana(przestaw(wybrane, i, 1))}
                className="rounded p-0.5 hover:bg-accent disabled:opacity-30"
              >
                <ArrowDown className="size-3.5" />
              </button>
              <button
                type="button"
                title={t('rapw.usun')}
                onClick={() => onZmiana(wybrane.filter((k) => k !== klucz))}
                className="rounded px-1 text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-1 text-xs font-medium">
          {t('rapw.dostepne')} <span className="text-muted-foreground">({dostepne.length})</span>
        </p>
        <div className="max-h-32 overflow-y-auto rounded-md border border-border">
          {dostepne.length === 0 && <p className="p-2 text-xs text-muted-foreground">{t('rapw.wszystkieWybrane')}</p>}
          {dostepne.map((k) => (
            <button
              key={k.klucz}
              type="button"
              onClick={() => onZmiana([...wybrane, k.klucz])}
              className="flex w-full items-baseline gap-2 border-b border-border/60 px-2 py-1 text-left text-xs last:border-0 hover:bg-accent"
            >
              <span className="text-muted-foreground">+</span>
              <span className="min-w-0 flex-1 truncate">{k.naglowek}</span>
              {/* Kolumny poza zestawem domyślnym warto oznaczyć — nie ma ich w pliku z ekranu. */}
              {!k.domyslna && <span className="shrink-0 text-muted-foreground">{t('rapw.poza')}</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
